import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import SidebarItem from "./SidebarItem";

/**
 * A titled group of navigation items.
 * Collapsed rail: "Main" shows each item as an icon; every other section is a
 * single icon that opens a flyout next to the rail (rendered in a portal,
 * because the aside's backdrop-filter traps position: fixed).
 */
const SidebarSection = ({
  title,
  icon: SectionIcon,
  items,
  currentPath,
  onNavigate,
  isCollapsed,
  itemMatchesPath = (item, path) => path === item.path,
}) => {
  const [showPopup, setShowPopup] = useState(false);
  const [popupTop, setPopupTop] = useState(0);
  const [popupLeft, setPopupLeft] = useState(0);
  const buttonRef = useRef(null);
  const popupRef = useRef(null);
  const isMainSection = title === "Main";
  const sectionActive = items.some((item) => itemMatchesPath(item, currentPath));

  const openPopup = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) {
      setPopupTop(rect.top);
      setPopupLeft(rect.right + 12);
    }
    setShowPopup((v) => !v);
  };

  // Close on outside click and on navigation.
  useEffect(() => {
    if (!showPopup) return undefined;
    const onDown = (e) => {
      if (popupRef.current?.contains(e.target) || buttonRef.current?.contains(e.target)) return;
      setShowPopup(false);
    };
    const onKey = (e) => e.key === "Escape" && setShowPopup(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [showPopup]);
  useEffect(() => setShowPopup(false), [currentPath]);

  if (!items.length) return null;

  const renderItems = (collapsed) =>
    items.map((item) => (
      <SidebarItem
        key={item.path}
        icon={item.icon}
        label={item.label}
        count={item.count}
        isActive={itemMatchesPath(item, currentPath)}
        onClick={() => onNavigate(item.path)}
        isCollapsed={collapsed}
      />
    ));

  if (isCollapsed) {
    if (isMainSection) return <div className="mb-3 space-y-1">{renderItems(true)}</div>;

    return (
      <div className="mb-1.5">
        <button
          ref={buttonRef}
          type="button"
          onClick={openPopup}
          title={title}
          aria-haspopup="menu"
          aria-expanded={showPopup}
          className={`group w-full h-9 flex items-center justify-center rounded-[11px] transition-colors ${
            showPopup || sectionActive ? "bg-hover text-ink" : "text-muted hover:bg-hover hover:text-ink"
          }`}
        >
          <span
            className={`w-[26px] h-[26px] rounded-lg flex items-center justify-center ${
              sectionActive ? "bg-grad shadow-glow text-on-accent" : "group-hover:text-accent"
            }`}
          >
            <SectionIcon className="w-[15px] h-[15px]" />
          </span>
        </button>

        {ReactDOM.createPortal(
          <AnimatePresence>
            {showPopup && (
              <motion.div
                ref={popupRef}
                role="menu"
                initial={{ opacity: 0, x: -8, scale: 0.97 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: -8, scale: 0.97 }}
                transition={{ duration: 0.16 }}
                style={{ position: "fixed", top: popupTop, left: popupLeft, zIndex: 9999 }}
                className="min-w-[220px] p-2 rounded-2xl bg-panel-solid border border-line2 shadow-modal"
              >
                <div className="px-2.5 pt-1.5 pb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-accent">
                  {title}
                </div>
                <div className="space-y-0.5">{renderItems(false)}</div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body,
        )}
      </div>
    );
  }

  return (
    <div className="mb-4">
      {title && (
        <div className="px-2.5 pb-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-faint">
          {title}
        </div>
      )}
      <div className="space-y-0.5">{renderItems(false)}</div>
    </div>
  );
};

export default SidebarSection;
